export const API_BASE = import.meta.env.VITE_API_URL || "https://api.banh-mi-escape.com";

interface ApiErrorResponse {
  error: string;
  message: string;
  success: false;
  details?: { fieldErrors?: Record<string, string[]> };
  /** 409 optimistic-lock conflict: when the record was last changed on the server. */
  changedAt?: string;
}

/** Structured error from API calls. Preserves status, error code, and field-level validation errors. */
export class ApiError extends Error {
  status: number;
  code: string;
  fieldErrors?: Record<string, string[]>;
  /** Set on a 409 conflict — the server's current updatedAt (Block 6.1 optimistic lock). */
  changedAt?: string;

  constructor(
    message: string,
    status: number,
    code: string,
    fieldErrors?: Record<string, string[]>,
    changedAt?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.changedAt = changedAt;
  }

  get isConflict() {
    return this.status === 409 && this.code === "conflict";
  }

  get isValidation() {
    return this.status === 400 && this.code === "validation_error";
  }
  get isAuth() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isServer() {
    return this.status >= 500;
  }
  get isNetwork() {
    return this.status === 0;
  }
}

// Module-level auth token, synced by authStore via setAuthToken().
// Avoids circular imports (authStore → client, client → authStore).
let _authToken: string | null = null;

/** Called by authStore to keep the in-memory token in sync */
export function setAuthToken(token: string | null): void {
  _authToken = token;
}

/** Returns auth headers for raw fetch calls (e.g., file uploads) */
export function getAuthHeaders(): Record<string, string> {
  return _authToken ? { Authorization: `Bearer ${_authToken}` } : {};
}

/**
 * POST multipart/form-data (the JSON `api` client can't send FormData).
 * Adds credentials + auth headers, throws on non-2xx parsing the error body,
 * and returns the parsed JSON. Used by all media-upload call sites.
 */
export async function uploadFormData<T>(path: string, fd: FormData): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    body: fd,
    credentials: "include",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message || "Upload failed");
  }
  return response.json() as Promise<T>;
}

/**
 * Fetch an authed binary response (PII photo, generated PDF/XML) and return the raw Blob.
 * The JSON `api` client can't hand back binary, and a plain `<img src>`/`<a href>` can't
 * carry the in-memory Bearer token (→ 401), so authed binary goes through here. The caller
 * owns the Blob (e.g. `URL.createObjectURL` + revoke on unmount). To open/download in a tab
 * use `openAuthedFile`, which wraps this.
 */
export async function fetchAuthedBlob(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<Blob> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: init?.method ?? "GET",
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message || `Failed to load file (${response.status})`);
  }
  return response.blob();
}

/**
 * Fetch an authed binary response and open it in a new tab (or download when `filename` is
 * given). Plain `<a href>` navigation can't carry the in-memory Bearer token (→ 401), so
 * authed file endpoints (invoice PDF, Pohoda XML) go through here.
 */
export async function openAuthedFile(
  path: string,
  filename?: string,
  init?: { method?: string; body?: unknown },
): Promise<void> {
  const blob = await fetchAuthedBlob(path, init);
  const url = URL.createObjectURL(blob);
  if (filename) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    method: string,
    path: string,
    data?: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...extraHeaders,
    };

    const options: RequestInit = {
      method,
      headers,
      credentials: "include",
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    let response: Response;
    try {
      response = await fetch(url, options);
    } catch {
      throw new ApiError(
        "Cannot connect to server. Check your internet connection.",
        0,
        "network_error",
      );
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw new ApiError(
        response.ok ? "Invalid response format" : `Request failed (${response.status})`,
        response.status,
        "parse_error",
      );
    }

    if (!response.ok) {
      const error = json as ApiErrorResponse;

      // On 401, clear in-memory token — authStore's catch blocks handle the rest
      if (response.status === 401) {
        _authToken = null;
      }

      throw new ApiError(
        error.message || "Request failed",
        response.status,
        error.error || "unknown",
        error.details?.fieldErrors,
        error.changedAt,
      );
    }

    return json as T;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  async post<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("POST", path, data);
  }

  async put<T>(path: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>("PUT", path, data, headers);
  }

  async patch<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, data);
  }

  async delete<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("DELETE", path, data);
  }
}

export const api = new ApiClient();

// Typed API hooks helpers
export interface ApiResponse<T> {
  data: T;
  success: true;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: true;
}
